/*  Night Guard product page — CMS-driven marketing page.
    ────────────────────────────────────────────────────────────────
    iter100.141 — Rewritten as a thin caller over the shared
    `product-page/sections.jsx` renderers. Every hero/section string
    now lives in `db.cms_content` under page slug `product_secure`
    and is editable via Admin → Landing Page → Content.

    Naming rule still holds:
      • Sub-menu / navigation label stays generic: "Night Guard".
      • On this PAGE, the product carries its brand identity
        "Invisique Secure" (no ™). Comparison rows and related-product
        cross-links stay in code — they're structural, not marketing
        copy.
*/
import React, { useEffect } from "react";
import { useProductPageCms } from "@/lib/useProductPageCms";
import {
    Hero, TrustBar, PainPoint, Benefits, Journey, ComparisonTable,
    Testimonials, DoctorCredibility, Faq, RelatedProducts, FinalCta,
} from "@/components/product-page/sections";
import GuidedCallbackForm from "@/components/site/GuidedCallbackForm";
import SeoHead from "@/components/site/SeoHead";


const BRAND = "Invisique Secure";
const CMS_PAGE = "product_secure";
const TEST_PREFIX = "secure";
const PRODUCT_SLUG = "night-guard";

/* Hero image — single owned asset (was 2-image gallery with rotator; user
   supplied one canonical product shot so we drop the rotator for a cleaner
   premium hero. Add a second entry here to re-enable auto-rotation.) */
const HERO_GALLERY = [
    {
        label: "Product",
        url: "https://customer-assets.emergentagent.com/job_code-fetch-32/artifacts/0anxdpfw_7f76ee4f-1ffc-4c4b-b539-7ba3136bd8e3_c50f0521-4677-479d-9a39-b12dff830357_raw.png",
        alt: "Invisique Secure night guard on crystal blocks — clench & grind protection",
    },
];

/* Comparison table structure. Cell values encode tone: "yes"/"warn"/
   "no"/"na", optionally suffixed with a custom label after a hyphen
   (e.g. "yes-Full"). Row order and column count are fixed here —
   changing them requires a code edit. */
const COMPARISON_HEADERS = [
    "Feature", BRAND, "Generic Store Guard", "No Guard At All",
];
const COMPARISON_ROWS = [
    ["Custom Fit", "yes", "no", "na"],
    ["Doctor Supervised", "yes", "no", "no"],
    ["Medical-Grade Material", "yes", "warn", "no"],
    ["BPA-Free", "yes", "warn", "na"],
    ["Enamel Protection", "yes-Full", "warn-Partial", "no-None"],
    ["Long-Term Comfort", "yes-Nightly", "no-Bulky", "na"],
    ["Professional Monitoring", "yes", "no", "no"],
];

const RELATED = [
    { title: "Invisique Serene", subtitle: "Anti-Snoring Guard", body: "For peaceful, uninterrupted sleep.", href: "/mouthguards-and-retainer/anti-snoring", icon: "Bed", tint: "from-sky-100 to-cyan-100", iconTint: "text-sky-600" },
    { title: "Invisique Apex", subtitle: "Sports Guard", body: "For athletes who need protection.", href: "/mouthguards-and-retainer/sports-guard", icon: "Barbell", tint: "from-amber-100 to-rose-100", iconTint: "text-amber-600" },
    { title: "Invisique Infinite", subtitle: "Infinite Retainer", body: "Lifetime hold on your new smile.", href: "/mouthguards-and-retainer/retainer", icon: "Sparkle", tint: "from-emerald-100 to-teal-100", iconTint: "text-emerald-600" },
];


export default function InvisiqueSecurePage() {
    const { t, arr, loading } = useProductPageCms(CMS_PAGE);

    useEffect(() => {
        // SeoHead below drives the actual document.title from CMS; this
        // useEffect is left blank on purpose so an old cached title
        // doesn't flash while the CMS payload loads.
    }, []);

    const scrollToLeadForm = () => {
        document.getElementById("guided-callback-anchor")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    // Wait for CMS payload before rendering so a) headlines don't flash
    // pre-fill fallbacks and b) admin edits are guaranteed to be honoured
    // on first paint. The skeleton is a neutral holding block.
    if (loading) {
        return <div className="min-h-[60vh] bg-white" aria-hidden="true" data-testid="secure-loading" />;
    }

    return (
        <div className="bg-white" data-testid="invisique-secure-page">
            <SeoHead
                page={CMS_PAGE}
                fallbackTitle={`${BRAND} · Custom Night Guard · Invisique`}
                fallbackDescription="Custom night guard for teeth grinding. Multi-layer polymer, doctor-prescribed."
            />
            <Hero
                t={t}
                arr={arr}
                testPrefix={TEST_PREFIX}
                gallery={HERO_GALLERY}
                halosTint="amber"
                captionIcon="Moon"
                credibilityIcon="Star"
            />
            <TrustBar t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <PainPoint t={t} arr={arr} testPrefix={TEST_PREFIX} layout="cols" />
            <Benefits t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <Journey t={t} arr={arr} testPrefix={TEST_PREFIX} bg="bg-white" />
            <ComparisonTable
                t={t}
                arr={arr}
                testPrefix={TEST_PREFIX}
                headers={COMPARISON_HEADERS}
                rows={COMPARISON_ROWS}
            />
            <Testimonials t={t} arr={arr} testPrefix={TEST_PREFIX} bg="bg-white" scope="product_secure" />
            <DoctorCredibility t={t} arr={arr} testPrefix={TEST_PREFIX} gradient="from-primary/[0.06] via-white to-indigo-50" />
            <Faq t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <GuidedCallbackForm
                productSlug={PRODUCT_SLUG}
                defaultInterest={PRODUCT_SLUG}
                themeTint="indigo"
                testPrefix={TEST_PREFIX}
            />
            <RelatedProducts
                t={t}
                arr={arr}
                testPrefix={TEST_PREFIX}
                items={RELATED}
            />
            <FinalCta
                t={t}
                testPrefix={TEST_PREFIX}
                onCallbackClick={scrollToLeadForm}
            />
        </div>
    );
}
